import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from "recharts";
import { motion } from "framer-motion";
import { METRICS } from "../lib/calc";
import { Maximize2, LineChart as LineChartIcon, Table2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const PCT = [
  { key: "P3", color: "#E63946", dash: "4 3" },
  { key: "P15", color: "#F4A261", dash: "4 3" },
  { key: "P50", color: "#2A9D8F", dash: "0" },
  { key: "P85", color: "#F4A261", dash: "4 3" },
  { key: "P97", color: "#E63946", dash: "4 3" },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const pt = payload.find((p) => p.dataKey === "patient");
  const ptDate = pt?.payload?.patientDate;
  const fmtD = (d) => { try { return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }); } catch { return null; } };
  return (
    <div className="bg-white rounded-xl shadow-lg border border-stone-100 px-3 py-2 text-xs font-nunito">
      <p className="font-bold text-brand-ink">{label} meses</p>
      {pt && pt.value != null && (
        <p className="text-brand-terracotta font-semibold mt-1">
          Paciente: {pt.value}{ptDate ? ` · ${fmtD(ptDate)}` : ""}
        </p>
      )}
      {payload.filter((p) => p.dataKey !== "patient").map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.dataKey}: {p.value}</p>
      ))}
    </div>
  );
}

function ChartBody({ data, height, domain }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 5, right: 12, left: -8, bottom: 5 }}>
        <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" vertical={false} />
        <XAxis dataKey="age" type="number" domain={domain || ["dataMin", "dataMax"]} allowDecimals={false}
          tick={{ fontSize: 11, fill: "#94A3B8" }} tickCount={8} label={{ value: "Edad (meses)", position: "insideBottom", offset: -2, fontSize: 11, fill: "#94A3B8" }} />
        <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} domain={["auto", "auto"]} />
        <Tooltip content={<CustomTooltip />} />
        {PCT.map((p) => (
          <Line key={p.key} type="monotone" dataKey={p.key} stroke={p.color}
            strokeWidth={p.key === "P50" ? 2.5 : 1.5} strokeDasharray={p.dash} dot={false} isAnimationActive={false} />
        ))}
        <Line type="monotone" dataKey="patient" stroke="#3D405B" strokeWidth={3}
          dot={{ r: 5, fill: "#E07A5F", stroke: "#fff", strokeWidth: 2 }} connectNulls isAnimationActive />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const Legend = () => (
  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
    <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#2A9D8F]" />P50 (mediana)</span>
    <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#F4A261]" />P15/P85</span>
    <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#E63946]" />P3/P97</span>
    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-brand-terracotta" />Paciente</span>
  </div>
);

const STATUS_STYLES = {
  normal: { label: "Normal", cls: "bg-brand-sage/15 text-brand-sage" },
  bajo: { label: "Bajo", cls: "bg-amber-100 text-amber-700" },
  alto: { label: "Alto", cls: "bg-amber-100 text-amber-700" },
  muy_bajo: { label: "Muy bajo", cls: "bg-red-100 text-red-600" },
  muy_alto: { label: "Muy alto", cls: "bg-red-100 text-red-600" },
};

function HistoryTable({ points, unit }) {
  if (!points?.length) {
    return <div className="h-80 flex items-center justify-center text-muted-foreground text-sm">Aún no hay mediciones registradas para esta métrica.</div>;
  }
  const rows = [...points].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  return (
    <div className="overflow-x-auto rounded-2xl border border-stone-100" data-testid="growth-history-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/60 text-brand-ink/70 text-xs font-bold">
            <th className="text-left px-4 py-2.5">Fecha</th>
            <th className="text-left px-4 py-2.5">Edad (meses)</th>
            <th className="text-left px-4 py-2.5">Valor</th>
            <th className="text-left px-4 py-2.5">Percentil</th>
            <th className="text-left px-4 py-2.5">Z-score</th>
            <th className="text-left px-4 py-2.5">Estado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => {
            const st = STATUS_STYLES[p.status] || STATUS_STYLES.normal;
            return (
              <tr key={i} className="border-t border-stone-100 hover:bg-muted/30 transition-colors" data-testid={`growth-history-row-${i}`}>
                <td className="px-4 py-2.5 font-medium text-brand-ink">{fmtDate(p.date)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{Math.round(p.age)}</td>
                <td className="px-4 py-2.5 font-bold text-brand-terracotta">{p.value} {unit || ""}</td>
                <td className="px-4 py-2.5 text-brand-ink">{p.percentile != null ? `P${Math.round(p.percentile)}` : "—"}</td>
                <td className="px-4 py-2.5 text-brand-ink">{p.z != null ? p.z : "—"}</td>
                <td className="px-4 py-2.5"><span className={`px-2.5 py-1 rounded-full text-xs font-bold ${st.cls}`}>{st.label}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function GrowthChart({ patientId, sex, ageMonths = 0 }) {
  const available = METRICS.filter((m) =>
    (m.key !== "imc" || ageMonths >= 24) && (m.key !== "perimetro_cefalico" || ageMonths < 36)
  );
  const [metric, setMetric] = useState("peso");
  const [standard, setStandard] = useState(ageMonths < 24 ? "who" : "cdc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [big, setBig] = useState(false);
  const [view, setView] = useState("chart");

  const UNITS = { peso: "kg", talla: "cm", perimetro_cefalico: "cm", imc: "" };
  const unit = UNITS[metric] || "";

  useEffect(() => {
    if (!available.find((m) => m.key === metric)) setMetric("peso");
  }, [ageMonths]); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/patients/${patientId}/growth`, { params: { metric, standard } });
      setData(res.data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [patientId, metric, standard]);

  useEffect(() => { load(); }, [load]);

  const merged = (() => {
    if (!data?.curves) return [];
    const map = {};
    data.curves.series.forEach((s) => { map[s.age] = { ...s }; });
    (data.patient_points || []).forEach((p) => {
      const n = Math.round(p.age);
      if (!map[n]) map[n] = { age: n };
      map[n].patient = p.value;
      map[n].patientDate = p.date;
    });
    return Object.values(map).sort((a, b) => a.age - b.age);
  })();

  const lastPoint = data?.patient_points?.[data.patient_points.length - 1];
  const xDomain = merged.length ? [Math.min(...merged.map((d) => d.age)), Math.max(...merged.map((d) => d.age))] : undefined;

  const Controls = () => (
    <div className="flex gap-2 flex-wrap">
      <div className="flex bg-muted rounded-full p-1">
        {["who", "cdc"].map((s) => (
          <button key={s} data-testid={`std-${s}`} onClick={() => setStandard(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${standard === s ? "bg-brand-sage text-white" : "text-brand-ink/60"}`}>
            {s.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="flex bg-muted rounded-full p-1 flex-wrap">
        {available.map((m) => (
          <button key={m.key} data-testid={`metric-${m.key}`} onClick={() => setMetric(m.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${metric === m.key ? "bg-brand-terracotta text-white" : "text-brand-ink/60"}`}>
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-outfit font-bold text-lg text-brand-ink">{data?.curves?.label || "Somatometría"}</h3>
          <p className="text-xs text-muted-foreground">Percentiles · {standard.toUpperCase()} · sexo {sex === "F" ? "femenino" : "masculino"}</p>
        </div>
        <div className="flex items-center gap-2">
          <Controls />
          <div className="flex bg-muted rounded-full p-1">
            <button data-testid="view-chart" onClick={() => setView("chart")} title="Gráfica"
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${view === "chart" ? "bg-brand-ink text-white" : "text-brand-ink/60"}`}>
              <LineChartIcon size={14} /> Gráfica
            </button>
            <button data-testid="view-history" onClick={() => setView("history")} title="Historial de crecimiento"
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${view === "history" ? "bg-brand-ink text-white" : "text-brand-ink/60"}`}>
              <Table2 size={14} /> Historial
            </button>
          </div>
          <button data-testid="expand-chart" onClick={() => setBig(true)} title="Ampliar para mostrar a los padres"
            className="flex items-center gap-1.5 bg-brand-ink text-white px-3 py-2 rounded-full text-xs font-bold hover:opacity-90 transition-all">
            <Maximize2 size={14} /> Ampliar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-80 flex items-center justify-center"><div className="w-8 h-8 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
      ) : view === "history" ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="growth-history">
          <HistoryTable points={data?.patient_points} unit={unit} />
        </motion.div>
      ) : !data?.curves ? (
        <div className="h-80 flex items-center justify-center text-muted-foreground text-sm">Sin datos de referencia para esta métrica/edad.</div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-80" data-testid="growth-chart">
          <ChartBody data={merged} height="100%" domain={xDomain} />
        </motion.div>
      )}

      <div className="mt-4">{view === "chart" && <Legend />}</div>

      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="rounded-3xl max-w-5xl w-[95vw]">
          <DialogHeader>
            <DialogTitle className="font-outfit text-brand-ink text-2xl">{data?.curves?.label}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Controls />
            {lastPoint && (
              <div className="text-sm bg-brand-terracotta/10 text-brand-terracotta font-bold px-4 py-2 rounded-full" data-testid="big-percentile">
                Última medición: {lastPoint.value} · Percentil {Math.round(lastPoint.percentile)} (z {lastPoint.z})
              </div>
            )}
          </div>
          <div className="h-[62vh] mt-2" data-testid="growth-chart-big">
            <ChartBody data={merged} height="100%" domain={xDomain} />
          </div>
          <Legend />
        </DialogContent>
      </Dialog>
    </div>
  );
}
